import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

async function fetchTechnicians() {
  const { data, error } = await supabase
    .from("technicians")
    .select("id,name,photo_url,status,phone,specializations")
    .order("name");
  if (error) throw error;
  return data ?? [];
}

export default function TechniciansList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: techs, isLoading, error } = useQuery({
    queryKey: ["technicians-list"],
    queryFn: fetchTechnicians,
  });

  const toggleStatus = useMutation({
    mutationFn: async ({ id, newStatus }: { id: string; newStatus: string }) => {
      const { error } = await supabase
        .from("technicians")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["technicians-list"] });
      toast.success("Status updated!");
    },
    onError: () => toast.error("Failed to update status."),
  });

  if (isLoading) return <div className="py-8 text-center">Loading technicians…</div>;
  if (error) return <div className="text-red-500 text-center py-8">Failed to load technicians.</div>;
  if (!techs.length) return <div className="py-8 text-center">No technicians found.</div>;

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
      {techs.map((t: any) => (
        <Card key={t.id} className="relative flex flex-col">
          <CardHeader className="flex-row items-center gap-4 p-4 pb-2">
            <Avatar className="h-12 w-12">
              {t.photo_url ? <AvatarImage src={t.photo_url} alt={t.name} /> : <AvatarFallback>{t.name?.slice(0,2).toUpperCase()}</AvatarFallback>}
            </Avatar>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">{t.name}</CardTitle>
              <CardDescription className="text-xs">
                {t.status === "active" ? <span className="text-teal-600">Active</span>
                  : t.status === "on_leave" ? <span className="text-yellow-600">On Leave</span>
                  : <span className="text-slate-400">Inactive</span>}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-0 px-4 pb-3 text-xs text-slate-600 flex-1">
            <div>
              <span className="font-medium">Phone:</span> {t.phone ? <a className="text-slate-900 underline" href={`tel:${t.phone}`}>{t.phone}</a> : <span className="text-slate-400">N/A</span>}
            </div>
            <div className="mt-2">
              <span className="font-medium">Specializations:</span>
              <span className="ml-1">
                {Array.isArray(t.specializations) && t.specializations.length ? t.specializations.join(", ") : <span className="text-slate-400">None</span>}
              </span>
            </div>
          </CardContent>
          <div className="flex items-center gap-2 px-4 pb-4">
            <Button size="sm" variant="outline" onClick={() => navigate(`/technicians/${t.id}`)}>View Profile</Button>
            {t.status === "active" ? (
              <Button size="sm" variant="secondary" onClick={() => toggleStatus.mutate({ id: t.id, newStatus: "inactive" })}>Deactivate</Button>
            ) : (
              <Button size="sm" variant="default" onClick={() => toggleStatus.mutate({ id: t.id, newStatus: "active" })}>Activate</Button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}